import { ChangeEvent, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLicensePools, useAssignLicenseManagers, useLicenses, useLinkContractManager, useAccountAdmins, useMyAssignedPools } from "hooks/useLicense";
import Cookies from "js-cookie";

import Hierarchy from "./Hierarchy";
import TableLicenses from "./TableLicenses";
import ViewLicense from "./ViewLicense";
import LicenseManagerModal from "./LicenseManagerModal";
import ManageSelectionModal from "./ManageSelectionModal";
import NoAssignedPoolsBanner from "./NoAssignedPoolsBanner";

import { FormData, Serial, SerialsData, LicenseManager } from "types/module/License";
import HeaderTitle from "components/ui/Header/HeaderTitle";
import { Button, Grid, Input, Modal, Menu } from "@mantine/core";
import { FaFilter } from "react-icons/fa";
import ModalButtons from "components/ui/Modal/ModalButtons";

import isEmpty from "lodash/isEmpty";
import filter from "lodash/filter";
import toLower from "lodash/toLower";
import { isNil } from "lodash";
import ContractManufacturerModal from "./ContractManufacturerModal";
import { getManagerEmail } from "helpers";

const SERIAL_DEFAULT: Serial = {
	status: null,
	serialNumber: null,
	contactName: null,
	accountName: null,
	pool_balance_threshold: null,
	notification_contacts: null,
	licenseManagers: [],
};

const License: React.FC = () => {
	const queryClient = useQueryClient();

	// Check if user has admin permissions based on accountType
	// Only admins and super users can assign license managers, not regular license managers
	const accountType = Cookies.get("accountType");
	const isLicenseManager = accountType === "oem_customer_license_manager";
	const childEditable = true;
	// is allowedAllocator should be for account types admins and super user
	const isAllowedAllocation = [
		"audinate_admin",
		"oem_customer_admin",
		"oem_customer_super_user",
	].includes(accountType);

	document.title = "License Pools";
	const [serials, setSerials] = useState<Serial[]>([]);
	const [licenseFilter, setLicenseFilter] = useState<string>("");
	const [serial, setSerial] = useState<Serial>(SERIAL_DEFAULT);
	const [isOpenLicenseModal, setIsOpenLicenseModal] = useState<boolean>(false);
	const [isSerialsEditable, setIsSerialsEditable] = useState<boolean>(false);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
	const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'assigned'>('all');
	const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
	const [isParentAccount, setIsParentAccount] = useState<boolean>(true);

	// License Manager states
	const [selectedPools, setSelectedPools] = useState<string[]>([]);
	const [isLicenseManagerModalOpen, setIsLicenseManagerModalOpen] = useState<boolean>(false);
	const [isContractManufacturerModalOpen, setIsContractManufacturerModalOpen] = useState<boolean>(false);
	const [isManageSelectionModalOpen, setIsManageSelectionModalOpen] = useState<boolean>(false);
	const [isRefetchingAfterCMChange, setIsRefetchingAfterCMChange] = useState<boolean>(false);
	const [isUpdatingCM, setIsUpdatingCM] = useState<boolean>(false);
	const [isLMOperationInProgress, setIsLMOperationInProgress] = useState<boolean>(false);

	// Ref mirror of isOpenLicenseModal — used in the serials watcher so the effect
	// doesn't re-fire (and trigger a /pools refetch) purely because the modal closed.
	const isModalOpenRef = useRef(false);

	// Get current user email for LM pool filtering
	const userEmail = Cookies.get("email");

	const {
		data: licenseHierarchyData,
		refetch: refetchLicenses,
		isFetching: isLicensesFetching,
	} = useLicenses();

	// Use PAM to get assigned pool UUIDs for LMs viewing "Assigned to me"
	// This is a pass-through from PAM - just returns pool UUIDs
	const {
		data: myAssignedPoolsData,
		isLoading: isMyAssignedPoolsLoading,
	} = useMyAssignedPools(
		userEmail || null,
		isLicenseManager && assignmentFilter === 'assigned'
	);

	// Check if LM has no assigned pools
	const lmHasNoPools = isLicenseManager && assignmentFilter === 'assigned' && !isMyAssignedPoolsLoading && (!myAssignedPoolsData?.pools || myAssignedPoolsData.pools.length === 0);

	// Helper to find pool data in license hierarchy by pool UUID
	const findPoolInLicenseHierarchy = useCallback((poolUuid: string, data: any): any => {
		if (!data) return null;

		// Check licenses at current level
		if (data.licenses && Array.isArray(data.licenses)) {
			for (const license of data.licenses) {
				const licensePoolId = license.serialNumber?.poolId || license.serialNumber;
				if (licensePoolId === poolUuid) {
					return {
						...license,
						accountName: data.name || data.accountName,
					};
				}
			}
		}

		// Recursively check accounts
		if (data.accounts && Array.isArray(data.accounts)) {
			for (const account of data.accounts) {
				const found = findPoolInLicenseHierarchy(poolUuid, account);
				if (found) return found;
			}
		}

		return null;
	}, []);

	// When "Assigned to me" is active, set serials from PAM response enriched with licenseHierarchyData
	useEffect(() => {
		if (isLicenseManager && assignmentFilter === 'assigned' && myAssignedPoolsData?.pools?.length > 0 && licenseHierarchyData) {
			// Enrich pool UUIDs with data from the cached license hierarchy
			const assignedSerials: Serial[] = myAssignedPoolsData.pools.map((poolUuid: string) => {
				const hierarchyPool = findPoolInLicenseHierarchy(poolUuid, licenseHierarchyData);

				if (hierarchyPool) {
					// Found in hierarchy - use enriched data
					return {
						status: hierarchyPool.status || null,
						serialNumber: hierarchyPool.serialNumber || poolUuid,
						contactName: hierarchyPool.contactName || null,
						accountName: hierarchyPool.accountName || null,
						pool_balance_threshold: hierarchyPool.pool_balance_threshold || null,
						notification_contacts: hierarchyPool.notification_contacts || null,
						licenseManagers: hierarchyPool.licenseManagers || [],
						parentPool: hierarchyPool.parentPool || null,
					};
				}

				// Not found in hierarchy - use minimal data
				return {
					status: null,
					serialNumber: poolUuid,
					contactName: null,
					accountName: null,
					pool_balance_threshold: null,
					notification_contacts: null,
					licenseManagers: [],
				};
			});
			setSerials(assignedSerials);
		}
	}, [isLicenseManager, assignmentFilter, myAssignedPoolsData, licenseHierarchyData, findPoolInLicenseHierarchy]);

	const {
		data: serialsData,
		isFetching: isSerialsDataLoading,
		refetch: serialsDataRefetch,
	} = useLicensePools(serials.map((item: Serial) => typeof item.serialNumber === 'string' ? item.serialNumber : item.serialNumber?.poolId));

	const {
		data: accountAdminsFromAPI,
	} = useAccountAdmins();

	const hierarchyLicenseManagers = useMemo(() => {
		if (!licenseHierarchyData) return [];

		const managersMap = new Map();

		const collectManagersFromNode = (node, accountName) => {
			if (node.licenseManagers && Array.isArray(node.licenseManagers)) {
				node.licenseManagers.forEach(manager => {
					const key = manager.Id || getManagerEmail(manager);
					managersMap.set(key, { ...manager, accountName });
				});
			}
		};

		collectManagersFromNode(licenseHierarchyData, licenseHierarchyData.name);

		if (selectedAccountId && !isParentAccount) {
			const findSelectedAccount = (node) => {
				if (node.accountId === selectedAccountId) {
					collectManagersFromNode(node, node.name);
					return true;
				}
				if (node.accounts && Array.isArray(node.accounts)) {
					for (const childAccount of node.accounts) {
						if (findSelectedAccount(childAccount)) {
							return true;
						}
					}
				}
				return false;
			};
			findSelectedAccount(licenseHierarchyData);
		}

		return Array.from(managersMap.values());
	}, [licenseHierarchyData, selectedAccountId, isParentAccount]);

	const licenseManagersData = useMemo(() => {
		const combinedMap = new Map();

		const managerExists = (newManager: any) => {
			const newEmail = getManagerEmail(newManager);
			return Array.from(combinedMap.values()).some(existing => {
				const existingEmail = getManagerEmail(existing);
				if (existingEmail && newEmail &&
					existingEmail.toLowerCase() === newEmail.toLowerCase()) return true;
				return false;
			});
		};

		hierarchyLicenseManagers.forEach(manager => {
			if (!managerExists(manager)) {
				const key = manager.Id || getManagerEmail(manager);
				combinedMap.set(key, manager);
			}
		});

		if (accountAdminsFromAPI) {
			accountAdminsFromAPI.forEach((admin: any) => {
				if (!managerExists(admin)) {
					const key = admin.Id || getManagerEmail(admin);
					combinedMap.set(key, { ...admin, accountName: "Admin" });
				}
			});
		}

		return Array.from(combinedMap.values());
	}, [hierarchyLicenseManagers, accountAdminsFromAPI]);

	// Account admins for notification contacts (API returns only admins now)
	const accountAdmins = useMemo(() => {
		return accountAdminsFromAPI || [];
	}, [accountAdminsFromAPI]);

	const {
		mutate: assignManagersMutate,
		isSuccess: assignManagersSuccess,
		isError: assignManagersError,
		isPending: isAssigningManagers,
	} = useAssignLicenseManagers();

	// Bulk CM operations: ContractManufacturerModal → multiple selected pools
	const {
		mutate: bulkLinkCMMutate,
		isSuccess: bulkLinkCMSuccess,
		isError: bulkLinkCMError,
		isPending: isBulkLinkingCM,
	} = useLinkContractManager();

	const handleSerialsData = (data: SerialsData[] | undefined) => {
		if (!data || isEmpty(data)) {
			return [];
		}

		const filtered = filter(data, (item) => {
			if (!licenseFilter) return true;

			const lowerCaseFilter = toLower(licenseFilter);

			return (
				toLower(item.name).includes(lowerCaseFilter) ||
				toLower(item.uuid).includes(lowerCaseFilter)
			);
		});

		// Merge PAM pool data (alias, balance) with SF hierarchy data (assignedTo, parentPool)
		// Data sources: alias & balance from PAM /pools, assignedTo & hierarchy from SF /licenses
		const mapped = filtered.map(item => {
			const matchingSerial = serials.find(serial => {
				const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
				return poolId === item.uuid;
			});

			let assignedTo = null;
			if (matchingSerial && typeof matchingSerial.serialNumber === 'object') {
				assignedTo = matchingSerial.serialNumber?.assignedTo;

				if (!assignedTo && matchingSerial.parentPool) {
					const parentSerial = serials.find(serial => {
						const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
						return poolId === matchingSerial.parentPool;
					});

					if (parentSerial && typeof parentSerial.serialNumber === 'object') {
						assignedTo = parentSerial.serialNumber?.assignedTo;
					} else {
						assignedTo = serials.length > 0 ? serials[0].accountName : null;
					}
				}
			}

			const result = {
				...item,
				assignedTo: assignedTo || undefined,
				parentPool: matchingSerial?.parentPool || null
			};

			return result;
		});

		return mapped;
	};

	const handleCloseModal = () => {
		if (hasUnsavedChanges) {
			setShowConfirmationModal(true);
			return;
		}
		setIsOpenLicenseModal(false);
	};

	const handleModalClose = useCallback((hadOperations: boolean) => {
		if (hadOperations) {
			queryClient.invalidateQueries({ queryKey: ["licenses"] });
			queryClient.invalidateQueries({ queryKey: ["licensePools"] });
			queryClient.invalidateQueries({ queryKey: ["licensePool"] }); // Invalidate individual pool queries (PAM data)
			// Explicitly refetch pools to ensure updated alias/balance from PAM is displayed
			serialsDataRefetch();
		}
	}, [queryClient, serialsDataRefetch]);

	const handleSelectAccount = (data: [boolean, Serial[], string]) => {
		if (data.length >= 3 && data[0] !== undefined && data[1] !== undefined && data[2] !== undefined) {
			setSerials(data[1]);
			setIsSerialsEditable(childEditable || data[0]);
			setSelectedAccountId(data[2]);
		}
	};

	// Keep the ref in sync with the modal state.
	useEffect(() => {
		isModalOpenRef.current = isOpenLicenseModal;
	}, [isOpenLicenseModal]);

	useEffect(() => {
		// Skip during modal — all pool data invalidation is deferred to modal close.
		// Guard uses a ref (not state) so this effect's deps stay as [serials, serialsDataRefetch].
		// If isOpenLicenseModal were a dep, the effect would re-fire on every modal close
		// (including "Yes" discard) and trigger an unwanted /pools refetch.
		if (serials.length > 0 && !isModalOpenRef.current) {
			serialsDataRefetch();
		}
	}, [serials, serialsDataRefetch]);

	useEffect(() => {
		if (isRefetchingAfterCMChange || isBulkLinkingCM || isUpdatingCM) return;

		if (serialsData && Array.isArray(serialsData) && licenseManagersData) {
			setSerials(prev => prev.map(serial => {
				const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
				const poolData = serialsData.find(pool => pool.uuid === poolId);
				const admins = accountAdminsFromAPI || [];

				// Use licenseManagers array from poolData (already enriched from PAM via hierarchy)
				if (poolData && poolData.licenseManagers && Array.isArray(poolData.licenseManagers) && poolData.licenseManagers.length > 0) {
					const managersToUse = poolData.licenseManagers;

					return {
						...serial,
						licenseManagers: managersToUse,
						// Don't override notification_contacts - SF handles it via LM assignment email
					};
				}

				// No LMs assigned from PAM - show admins in UI for display purposes
				// but DON'T override notification_contacts - preserve SF value
				return {
					...serial,
					licenseManagers: admins,
					// notification_contacts is preserved from the original serial data (SF source)
				};
			}));
		}
	}, [serialsData, licenseManagersData, isRefetchingAfterCMChange, isBulkLinkingCM, isUpdatingCM]);

	useEffect(() => {
		if (licenseHierarchyData && serials.length > 0) {
			const enrichedSerials = [...serials];

			if (licenseHierarchyData.licenses && Array.isArray(licenseHierarchyData.licenses)) {
				licenseHierarchyData.licenses.forEach((license: any) => {
					const poolId = license.serialNumber?.poolId;

					// Find matching serial in enrichedSerials
					const matchingSerialIndex = enrichedSerials.findIndex(s => {
						const serialPoolId = typeof s.serialNumber === 'string' ? s.serialNumber : (s.serialNumber as any)?.poolId;
						return serialPoolId === poolId;
					});

					if (matchingSerialIndex !== -1) {
						const currentSerial = enrichedSerials[matchingSerialIndex];
						if (typeof currentSerial.serialNumber === 'object') {
							const serialNumObj = currentSerial.serialNumber as any;
							enrichedSerials[matchingSerialIndex].serialNumber = {
								...serialNumObj,
								assignedTo: license.serialNumber?.assignedTo || undefined,
								linkedPoolId: license.serialNumber?.linkedPoolId || undefined,
							};
							enrichedSerials[matchingSerialIndex].parentPool = license.parentPool || undefined;
							enrichedSerials[matchingSerialIndex].notification_contacts = license.notification_contacts || enrichedSerials[matchingSerialIndex].notification_contacts;
						}
					}
				});
			}

			const enrichWithCMData = (accounts: any[], parentPoolId?: string, depth = 0): void => {
				accounts.forEach((account) => {
					// Check if this account is in our enrichedSerials list
					const matchingSerialIndex = enrichedSerials.findIndex(s => {
						const serialPoolId = typeof s.serialNumber === 'string' ? s.serialNumber : (s.serialNumber as any)?.poolId;
						return serialPoolId === account.poolId;
					});

					if (matchingSerialIndex !== -1) {
						const currentSerial = enrichedSerials[matchingSerialIndex];

						if (typeof currentSerial.serialNumber === 'object') {
							const serialNumObj = currentSerial.serialNumber as any;
							enrichedSerials[matchingSerialIndex].serialNumber = {
								...serialNumObj,
								assignedTo: account.assignedTo || undefined,
								linkedPoolId: account.linkedPoolId || undefined,
							};
							enrichedSerials[matchingSerialIndex].parentPool = account.parentPoolId || parentPoolId || undefined;
							enrichedSerials[matchingSerialIndex].notification_contacts = account.notification_contacts || enrichedSerials[matchingSerialIndex].notification_contacts;
						}
					}

					// Recursively process child accounts
					if (account.accounts && Array.isArray(account.accounts)) {
						enrichWithCMData(account.accounts, account.poolId, depth + 1);
					}
				});
			};

			if (licenseHierarchyData.accounts && Array.isArray(licenseHierarchyData.accounts)) {
				enrichWithCMData(licenseHierarchyData.accounts);
			}

			setSerials(enrichedSerials);
		}
	}, [licenseHierarchyData]);

	useEffect(() => {
		if (isRefetchingAfterCMChange || isBulkLinkingCM || isUpdatingCM) return;

		if (isOpenLicenseModal && serial && serials.length > 0) {
			const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
			const updatedSerial = serials.find((item) => {
				const itemPoolId = typeof item.serialNumber === 'string' ? item.serialNumber : item.serialNumber?.poolId;
				return itemPoolId === poolId;
			});
			if (updatedSerial) {
				setSerial(updatedSerial);
			}
		}
	}, [serials, isOpenLicenseModal, isRefetchingAfterCMChange, isBulkLinkingCM, isUpdatingCM]);

	const tableData = useMemo(() => {
		return serials.length > 0 && serialsData ? handleSerialsData(serialsData) : [];
	}, [serials, serialsData, licenseFilter]);

	// Helper function to find a pool in the license hierarchy and check restrictPoolAssignment
	const findPoolInHierarchy = useCallback((poolId: string, hierarchyData: any): boolean => {
		// Check root level licenses
		if (hierarchyData.licenses) {
			const rootLicense = hierarchyData.licenses.find((license: any) =>
				license.serialNumber?.poolId === poolId
			);
			if (rootLicense) {
				return rootLicense.restrictPoolAssignment === true;
			}
		}

		// Check in accounts hierarchy
		const checkAccounts = (accounts: any[]): boolean => {
			for (const account of accounts) {
				// Check account's licenses
				if (account.licenses) {
					const accountLicense = account.licenses.find((license: any) =>
						license.serialNumber?.poolId === poolId
					);
					if (accountLicense) {
						return accountLicense.restrictPoolAssignment === true;
					}
				}

				// Recursively check child accounts
				if (account.accounts && account.accounts.length > 0) {
					if (checkAccounts(account.accounts)) {
						return true;
					}
				}
			}
			return false;
		};

		if (hierarchyData.accounts) {
			return checkAccounts(hierarchyData.accounts);
		}

		return false;
	}, []);

	const disableCMAssignment = useMemo(() => {
		if (!isParentAccount) return true;

		return selectedPools.some(poolId => {
			if (!licenseHierarchyData) return false;
			return findPoolInHierarchy(poolId, licenseHierarchyData);
		});
	}, [isParentAccount, selectedPools, licenseHierarchyData, findPoolInHierarchy]);

	const getDisableCMAssignmentForPool = useCallback((pool: any) => {
		if (!isParentAccount) return true;

		const poolId = typeof pool.serialNumber === 'string' ? pool.serialNumber : pool.serialNumber?.poolId;
		if (!poolId || !licenseHierarchyData) return false;

		return findPoolInHierarchy(poolId, licenseHierarchyData);
	}, [isParentAccount, licenseHierarchyData, findPoolInHierarchy]);

	useEffect(() => {
		if (selectedAccountId && licenseHierarchyData) {
			const isParent = selectedAccountId === licenseHierarchyData.accountId;
			setIsParentAccount(isParent);
			setSelectedPools([]);

			// Re-extract serials from updated hierarchy data when data refreshes
			// This ensures linked pools show up after "Refresh License Pool Balances"
			const findAccountById = (node: any, targetId: string): any => {
				if (node.accountId === targetId) return node;
				if (node.accounts && Array.isArray(node.accounts)) {
					for (const child of node.accounts) {
						const found = findAccountById(child, targetId);
						if (found) return found;
					}
				}
				return null;
			};

			const selectedAccount = findAccountById(licenseHierarchyData, selectedAccountId);
			if (selectedAccount && selectedAccount.licenses) {
				const updatedSerials: Serial[] = selectedAccount.licenses.map((license: any) => ({
					status: license.status || null,
					serialNumber: license.serialNumber || null,
					contactName: license.contactName || null,
					accountName: selectedAccount.name || null,
					pool_balance_threshold: license.pool_balance_threshold || null,
					notification_contacts: license.notification_contacts || null,
					licenseManagers: license.licenseManagers || [],
					parentPool: license.parentPool || null,
				}));
				setSerials(updatedSerials);
				setIsSerialsEditable(childEditable || isParent);
			}
		}
	}, [selectedAccountId, licenseHierarchyData, childEditable]);

	const handleFormChange = (data: FormData) => {
		const adminAndLMEmails = serial?.licenseManagers?.map(m => getManagerEmail(m)).filter(Boolean) || [];
		// Filter LM/admin emails from both sides so comparison is symmetric
		const storedContacts = serial?.notification_contacts?.split(";").filter(c => c && !adminAndLMEmails.includes(c)) || [];
		const formContacts = (data?.contacts || []).filter(c => !adminAndLMEmails.includes(c));

		const isFormModified =
			(data?.threshold != serial?.pool_balance_threshold && !isNil(serial?.pool_balance_threshold)) ||
			formContacts.join(";") !== storedContacts.join(";");

		const isCMChanged = data?.isCMChanged || false;
		const isAliasChanged = data?.aliasChanged || false;
		setHasUnsavedChanges(isFormModified || isCMChanged || isAliasChanged);
	};

	const handleLicenseSearch = (e: ChangeEvent<HTMLInputElement>): void => {
		setLicenseFilter(e.target.value);
	};

	const handleAddLicenseManagers = (managers: LicenseManager[]) => {
		const licenseManagers = managers.map(m => ({
			email: getManagerEmail(m),
			name: m.name
		})).filter(m => m.email); // Filter out any without email

		assignManagersMutate({
			poolUuids: selectedPools,
			licenseManagers,
		});
	};

	const handleAddMoreManagersToPool = (managers: LicenseManager[]) => {
		const newManagers = managers.map(m => ({
			email: getManagerEmail(m),
			name: m.name
		})).filter(m => m.email);

		assignManagersMutate({
			poolUuids: [typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId],
			licenseManagers: newManagers,
		});
	};

	const handleDirectAssignManagers = (poolUuids: string[], managers: LicenseManager[]) => {
		const licenseManagers = managers.map(m => ({
			email: getManagerEmail(m),
			name: m.name
		})).filter(m => m.email);

		assignManagersMutate({
			poolUuids,
			licenseManagers,
		});
	};

	useEffect(() => {
		if (assignManagersSuccess) {
			setIsLicenseManagerModalOpen(false);
			setSelectedPools([]);
			queryClient.invalidateQueries({ queryKey: ["licenses"] });
			queryClient.invalidateQueries({ queryKey: ["licensePools"] });
		}
	}, [assignManagersSuccess]);

	useEffect(() => {
		if (assignManagersError) {
			setIsLicenseManagerModalOpen(false);
		}
	}, [assignManagersError]);

	const handleOpenManageSelectionModal = () => {
		if (selectedPools.length === 0) return;
		setIsManageSelectionModalOpen(true);
	};

	const handleSelectLicenseManagers = () => {
		setIsManageSelectionModalOpen(false);
		setIsLicenseManagerModalOpen(true);
	};

	const handleSelectContractManager = () => {
		setIsManageSelectionModalOpen(false);
		setIsContractManufacturerModalOpen(true);
	};

	const handleAssignContractManager = (accountId: string) => {
		bulkLinkCMMutate({
			sfAccountId: accountId,
			targetPools: selectedPools,
			aId: Cookies.get("aId"),
		});
	};

	// Bulk CM link success → overlay + full hierarchy refetch
	useEffect(() => {
		if (bulkLinkCMSuccess) {
			const handleBulkLinkSuccess = async () => {
				setIsRefetchingAfterCMChange(true);
				setIsContractManufacturerModalOpen(false);
				setSelectedPools([]);
				setHasUnsavedChanges(false);
				await refetchLicenses();
				await serialsDataRefetch();
			};
			handleBulkLinkSuccess();
		}
	}, [bulkLinkCMSuccess, serialsDataRefetch, refetchLicenses]);

	useEffect(() => {
		if (bulkLinkCMError) {
			setIsContractManufacturerModalOpen(false);
		}
	}, [bulkLinkCMError]);

	// Clear the bulk CM loading state only after ALL downstream revalidations have settled.
	// A 300ms debounce absorbs the brief gap between the initial awaited fetches and the
	// secondary fetches triggered by licenseHierarchyData → setSerials → serialsDataRefetch.
	useEffect(() => {
		if (!isRefetchingAfterCMChange) return;
		if (isSerialsDataLoading || isLicensesFetching) return;
		const timer = setTimeout(() => setIsRefetchingAfterCMChange(false), 300);
		return () => clearTimeout(timer);
	}, [isRefetchingAfterCMChange, isSerialsDataLoading, isLicensesFetching]);

	useEffect(() => {
		if (selectedAccountId && licenseHierarchyData) {
			setSelectedPools([]);
		}
	}, [licenseHierarchyData, selectedAccountId]);

	const extractAccounts = (data: any): any[] => {
		if (!data || !data.accounts) return [];

		const accounts: any[] = [];
		data.accounts.forEach((item: any) => {
			if (item.accountId && item.name) {
				accounts.push({
					accountId: item.accountId,
					name: item.name,
				});
			}
		});

		return accounts;
	};

	const availableAccounts = extractAccounts(licenseHierarchyData);

	const handleViewAllPools = () => {
		setAssignmentFilter('all');
	};

	// If LM has no assigned pools, show centered banner instead of table and hierarchy
	if (lmHasNoPools) {
		return (
			<>
				<HeaderTitle title="License Pools">
					<Menu shadow="md" width={200}>
						<Menu.Target>
							<Button
								variant="default"
								leftSection={<FaFilter size={14} />}
							>
								Assigned to me
							</Button>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item onClick={handleViewAllPools}>
								All
							</Menu.Item>
							<Menu.Item fw={700}>
								Assigned to me
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</HeaderTitle>
				<NoAssignedPoolsBanner onViewAllPools={handleViewAllPools} />
			</>
		);
	}

	const showHierarchy = !isLicenseManager || assignmentFilter === 'all';

	return (
		<>
			<Grid>
				{showHierarchy && (
					<Grid.Col span={{ md: 2.5 }}>
						<Hierarchy

							handleSelectAccount={handleSelectAccount}
							assignmentFilter={isLicenseManager ? assignmentFilter : 'all'}
						/>
					</Grid.Col>
				)}
				<Grid.Col span={{ md: showHierarchy ? 9.5 : 12 }}>
					<HeaderTitle title="License Pools">
						<Input
							placeholder="Search Licenses..."
							value={licenseFilter}
							onChange={handleLicenseSearch}
						/>
						{isLicenseManager && (
							<Menu shadow="md" width={200}>
								<Menu.Target>
									<Button
										variant="default"
										leftSection={<FaFilter size={14} />}
									>
										{assignmentFilter === 'all' ? 'All' : 'Assigned to me'}
									</Button>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item
										onClick={() => setAssignmentFilter('all')}
										fw={assignmentFilter === 'all' ? 700 : 400}
									>
										All
									</Menu.Item>
									<Menu.Item
										onClick={() => setAssignmentFilter('assigned')}
										fw={assignmentFilter === 'assigned' ? 700 : 400}
									>
										Assigned to me
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						)}
						<Button
							color="orange"
							loading={isSerialsDataLoading || isRefetchingAfterCMChange || isMyAssignedPoolsLoading}
							onClick={() => {
								setLicenseFilter("");
								queryClient.invalidateQueries({ queryKey: ["permissions"] });
								setLicenseFilter("");
								queryClient.invalidateQueries({ queryKey: ["licenses"] });
								queryClient.invalidateQueries({ queryKey: ["licensePools"] });
							}}
						>
							Refresh License Pool Balances
						</Button>
						{childEditable && serials.length > 0 && isAllowedAllocation && (
							<Button
								color="blue"
								onClick={handleOpenManageSelectionModal}
								disabled={selectedPools.length === 0}
							>
								Manage Selected Licenses ({selectedPools.length} selected)
							</Button>
						)}
					</HeaderTitle>
					<TableLicenses
						serialsData={tableData}
						isSerialsDataLoading={isSerialsDataLoading || isRefetchingAfterCMChange || isLicensesFetching || (isLicenseManager && assignmentFilter === 'assigned' && isMyAssignedPoolsLoading)}
						handleSelectLicense={(serial: string) => {
							setSerial(serials.find((item) => item.serialNumber === serial || (typeof item.serialNumber === 'object' && item.serialNumber?.poolId === serial)));
							setIsOpenLicenseModal(true);
						}}
						showCheckboxes={childEditable && isAllowedAllocation}
						selectedPools={selectedPools}
						onPoolSelectionChange={setSelectedPools}
					/>
				</Grid.Col>
			</Grid>
			<Modal
				size="70%"
				title="View License Pool"
				opened={isOpenLicenseModal}
				onClose={handleCloseModal}
				closeOnClickOutside={!isUpdatingCM && !isLMOperationInProgress}
				closeOnEscape={!isUpdatingCM && !isLMOperationInProgress}
				withCloseButton={!isUpdatingCM && !isLMOperationInProgress}
				centered
			>
				{serial?.serialNumber && (
					<ViewLicense
						serial={serial}
						setSerials={setSerials}
						isEditable={isSerialsEditable}
						isOpenLicenseModal={isOpenLicenseModal}
						handleFormChange={handleFormChange}
						hasUnsavedChanges={hasUnsavedChanges}
						setHasUnsavedChanges={setHasUnsavedChanges}
						availableManagers={licenseManagersData || []}
						onAddMoreManagers={handleAddMoreManagersToPool}
						onDirectAssignManagers={handleDirectAssignManagers}
						assignManagersSuccess={assignManagersSuccess}
						isAssigningManagers={isAssigningManagers}
						availableAccounts={availableAccounts}
						currentCMAssignment={(() => {
							const poolId = typeof serial.serialNumber === 'object' ? serial.serialNumber?.poolId : serial.serialNumber;
							const enrichedSerial = tableData.find(item => item.uuid === poolId);
							return enrichedSerial?.assignedTo || '';
						})()}
						linkedPoolId={typeof serial.serialNumber === 'object' ? serial.serialNumber?.linkedPoolId || '' : ''}
						parentPool={serial?.parentPool || ''}
						isUpdatingCM={isUpdatingCM}
						setIsUpdatingCM={setIsUpdatingCM}
						isLicensesFetching={isLicensesFetching}
						isParentAccount={isParentAccount}
						disableCMAssignmentForPool={getDisableCMAssignmentForPool}
						accountAdmins={accountAdmins}
						onModalClose={handleModalClose}
						onLMOperationChange={setIsLMOperationInProgress}
						isAllowedAllocation={isAllowedAllocation}
					/>
				)}
			</Modal>
			<Modal
				opened={showConfirmationModal}
				title="Unsaved Changes"
				onClose={() => setShowConfirmationModal(false)}
				centered
			>
				You have unsaved changes that will be lost if you close this window.
				Do you wish to proceed?
				<ModalButtons>
					<Button variant="default" onClick={() => setShowConfirmationModal(false)}>
						No
					</Button>
					<Button
						color="orange"
						onClick={() => {
							setShowConfirmationModal(false);
							setIsOpenLicenseModal(false);
						}}
					>
						Yes
					</Button>
				</ModalButtons>
			</Modal>
			<LicenseManagerModal
				opened={isLicenseManagerModalOpen}
				onClose={() => setIsLicenseManagerModalOpen(false)}
				availableManagers={licenseManagersData || []}
				selectedPools={selectedPools}
				onAddManagers={handleAddLicenseManagers}
				isLoading={isAssigningManagers}
			/>
			<ManageSelectionModal
				opened={isManageSelectionModalOpen}
				onClose={() => setIsManageSelectionModalOpen(false)}
				onSelectLicenseManagers={handleSelectLicenseManagers}
				onSelectContractManager={handleSelectContractManager}
				selectedCount={selectedPools.length}
				disableCMAssignment={disableCMAssignment}
			/>
			<ContractManufacturerModal
				opened={isContractManufacturerModalOpen}
				onClose={() => setIsContractManufacturerModalOpen(false)}
				accounts={availableAccounts}
				onAssign={handleAssignContractManager}
				isLoading={isBulkLinkingCM}
			/>
		</>
	);
};

export default License;
